<template>
    <div class="chart-date-picker-wrapper">
        <advanced-date-picker
            :model-value="selectedDate"
            :default-model-value="props.defaultSelectedDate"
            :date-selection-locked="props.dateLocked"
            :timezone-selection-locked="props.timezoneLocked"
            :period-frequency-selection-locked="props.periodFrequencyLocked"
            :allow-select-timezone="props.showTimezone"
            :granularity-mode="props.granularityMode"
            :index-granularity-warning="props.indexGranularityWarning"
            :granularity-locked="props.granularityLocked"
            :timezone-id="selectedTimezoneId"
            :period-frequency="selectedPeriodsPerDay"
            :only-show-single-day="props.onlyAllowSingleDay"
            :minimum-view="props.minimumView"
            :min-date="props.minDate"
            :max-date="props.maxDate"
            :days-only="props.daysOnly"
            @submit="submitUpdate"
        />
    </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import { IAdvancedDatePickerModel, IAdvancedDatePickerUpdatePayload } from '@/interfaces/general/IAdvancedDatePickerModel.ts';
import { TDatePickerMinimumView } from '@/types/powerMarkets/TDatePickerMinimumView.ts';
import { TDatePickerGranularityMode } from '@/types/powerMarkets/TDatePickerGranularityMode.ts';
import AdvancedDatePicker from '@/components/powerMarkets/date-picker/AdvancedDatePicker.vue';
import { AdvancedDatePickerTabsEnum } from '@/enums/general/AdvancedDatePickerTabsEnum.ts';
import { useUserSettingsStore } from '@/stores/userSettingsStore.ts';

interface IProps {
    defaultSelectedDate?: IAdvancedDatePickerModel | null;
    dateLocked?: boolean;
    timezoneId?: string;
    periodsPerDay?: number;
    dashboardId?: string;
    moduleId?: string;
    timezoneLocked?: boolean;
    periodFrequencyLocked?: boolean;
    showTimezone?: boolean;
    granularityMode?: TDatePickerGranularityMode;
    indexGranularityWarning?: string;
    granularityLocked?: boolean;
    onlyAllowSingleDay?: boolean;
    minimumView?: TDatePickerMinimumView;
    daysOnly?: boolean;
    minDate?: Date;
    maxDate?: Date;
}

const props = defineProps<IProps>();

const emit = defineEmits<{
    'update:selected-date': [IAdvancedDatePickerUpdatePayload];
    'update:timezone-settings': [IAdvancedDatePickerUpdatePayload];
    'close:picker': [];
}>();

const userSettingsStore = useUserSettingsStore();

const selectedDate = defineModel<IAdvancedDatePickerModel | null>('selectedDate', { required: true });
const selectedTimezoneId = computed<string | undefined>(() => props.timezoneId ?? selectedDate.value?.timezone ?? undefined);
const selectedPeriodsPerDay = computed<number | undefined>(() => props.periodsPerDay ?? selectedDate.value?.periodsPerDay ?? undefined);

function getDateOptionsPayload (payload: IAdvancedDatePickerModel): IAdvancedDatePickerUpdatePayload {
    return {
        aggregate: payload.aggregate ?? null,
        dateRange: payload.dateRange,
        dateType: payload.dateType,
        rangeSelectType: payload.rangeSelectType,
        isHourRangeEnabled: payload.isHourRangeEnabled,
        hoursBefore: payload.hoursBefore ?? null,
        hoursAfter: payload.hoursAfter ?? null,
        daysBefore: payload.daysBefore ?? null,
        daysAfter: payload.daysAfter ?? null,
        variableDateStartString: payload.variableDateStartString,
        variableDateEndString: payload.variableDateEndString,
    };
}

async function submitUpdate (payload: IAdvancedDatePickerModel, submittedTab: AdvancedDatePickerTabsEnum): Promise<void> {
    if (submittedTab === AdvancedDatePickerTabsEnum.TIMEZONE) {
        if (props.dashboardId) {
            await userSettingsStore.updateDashboardTimezoneAndPeriodFrequency(props.dashboardId, {
                timezoneId: payload.timezone ?? null,
                periodsPerDay: payload.periodsPerDay ?? null,
                moduleId: props.moduleId,
            });
        }

        emit('update:timezone-settings', {
            timezone: payload.timezone ?? null,
            periodsPerDay: payload.periodsPerDay ?? null,
        });
        emit('close:picker');
        return;
    }

    emit('update:selected-date', getDateOptionsPayload(payload));
    emit('close:picker');
}

</script>

<style lang="scss" scoped>

.chart-date-picker-wrapper {
    padding: var(--defaultSpacing);
    width: fit-content;
    max-width: 90vw;
    box-sizing: border-box;
}

</style>
